import { Controller } from "@hotwired/stimulus";

/**
 * Cookie Preferences Controller
 * Handles user cookie consent preferences (preferences, analytics, marketing)
 */
export default class extends Controller {
  static targets = ["preferences", "analytics", "embeds", "marketing"];

  connect() {
    this.loadPreferences();
  }

  /**
   * Load saved preferences from cookie
   */
  loadPreferences() {
    const preferences = this.getPreferences();

    if (this.hasPreferencesTarget) {
      this.preferencesTarget.checked = preferences.preferences;
    }

    if (this.hasAnalyticsTarget) {
      this.analyticsTarget.checked = preferences.analytics;
    }

    if (this.hasEmbedsTarget) {
      this.embedsTarget.checked = preferences.embeds;
    }

    if (this.hasMarketingTarget) {
      this.marketingTarget.checked = preferences.marketing;
    }
  }

  /**
   * Get current preferences from cookie or defaults
   */
  getPreferences() {
    const cookie = document.cookie
      .split("; ")
      .find((row) => row.startsWith("cookie_preferences="));

    if (cookie) {
      try {
        return JSON.parse(decodeURIComponent(cookie.split("=")[1]));
      } catch (e) {
        console.error("Failed to parse cookie preferences:", e);
      }
    }

    // Default: all disabled except essential
    return {
      essential: true,
      preferences: false,
      analytics: false,
      embeds: false,
      marketing: false
    };
  }

  /**
   * Accept all cookies and close modal
   */
  acceptAll(event) {
    if (event) event.preventDefault();

    const preferences = {
      essential: true,
      preferences: true,
      analytics: true,
      embeds: true,
      marketing: true
    };

    this.setPreferencesCookie(preferences);
    this.activateScripts(preferences);
    this.dispatchPreferencesUpdated(preferences);
    this.closeModal();
    this.hideBanner();
  }

  /**
   * Reject all non-essential cookies and close modal
   */
  rejectAll(event) {
    if (event) event.preventDefault();

    const preferences = {
      essential: true,
      preferences: false,
      analytics: false,
      embeds: false,
      marketing: false
    };

    this.setPreferencesCookie(preferences);
    this.activateScripts(preferences);
    this.dispatchPreferencesUpdated(preferences);
    this.closeModal();
    this.hideBanner();
  }

  /**
   * Save current preferences and close modal
   */
  savePreferences(event) {
    if (event) event.preventDefault();

    const preferences = {
      essential: true, // Always true
      preferences: this.hasPreferencesTarget ? this.preferencesTarget.checked : false,
      analytics: this.hasAnalyticsTarget ? this.analyticsTarget.checked : false,
      embeds: this.hasEmbedsTarget ? this.embedsTarget.checked : false,
      marketing: this.hasMarketingTarget ? this.marketingTarget.checked : false
    };

    this.setPreferencesCookie(preferences);
    this.activateScripts(preferences);
    this.dispatchPreferencesUpdated(preferences);
    this.closeModal();
    this.hideBanner();
  }

  /**
   * Toggle handler for individual cookie type checkboxes
   */
  toggle(event) {
    // This is just for UI feedback - actual save happens on button click
  }

  /**
   * Set cookie preferences cookie
   */
  setPreferencesCookie(preferences) {
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1); // 1 year

    // Add Secure flag only in HTTPS (production)
    const secure = window.location.protocol === "https:" ? "; Secure" : "";

    // Share cookie across all subdomains (www, app, admin)
    const domain = this.getCookieDomain();

    document.cookie = `cookie_preferences=${encodeURIComponent(JSON.stringify(preferences))}; expires=${expires.toUTCString()}; path=/; domain=${domain}; SameSite=Lax${secure}`;

    // Also set the consent cookie
    document.cookie = `cookie_consent=accepted; expires=${expires.toUTCString()}; path=/; domain=${domain}; SameSite=Lax${secure}`;
  }

  /**
   * Get the root cookie domain for cross-subdomain sharing
   * @returns {string} Cookie domain with leading dot
   */
  getCookieDomain() {
    const parts = window.location.hostname.split(".");
    return "." + parts.slice(-2).join(".");
  }

  /**
   * Activate scripts based on preferences
   */
  activateScripts(preferences) {
    // Always activate required/essential scripts
    this.activateScriptsByType("required");

    // Activate preferences/functional scripts
    if (preferences.preferences) {
      this.activateScriptsByType("preferences");
    }

    // Activate analytics scripts
    if (preferences.analytics) {
      this.activateScriptsByType("analytics");
    }

    // Activate embed scripts (YouTube, etc.)
    if (preferences.embeds) {
      this.activateScriptsByType("embeds");
      // Dispatch custom event to notify video components
      document.dispatchEvent(new CustomEvent("cookie:embeds-accepted"));
    }

    // Activate marketing scripts
    if (preferences.marketing) {
      this.activateScriptsByType("marketing");
    }
  }

  /**
   * Activate scripts of a specific type
   */
  activateScriptsByType(type) {
    const blockedScripts = document.querySelectorAll(
      `script[type="text/plain"][data-cookie-consent="${type}"]`
    );

    blockedScripts.forEach((script) => {
      const newScript = document.createElement("script");
      newScript.textContent = script.textContent;

      // Copy attributes
      Array.from(script.attributes).forEach((attr) => {
        if (attr.name !== "type" && attr.name !== "data-cookie-consent") {
          newScript.setAttribute(attr.name, attr.value);
        }
      });

      script.parentNode.replaceChild(newScript, script);
    });
  }

  /**
   * Close the modal
   */
  closeModal() {
    const modal = document.getElementById("cookie-preferences-modal");
    if (modal) {
      modal.dispatchEvent(new CustomEvent("modal:close", { bubbles: true }));
    }
  }

  /**
   * Hide the cookie banner
   */
  hideBanner() {
    const banner = document.getElementById("cookie-banner");
    if (banner) {
      const controller = this.application.getControllerForElementAndIdentifier(
        banner,
        "banner"
      );
      if (controller && controller.hide) {
        controller.hide();
      }
    }
  }

  /**
   * Dispatch a global event when preferences change
   * Allows integrations (like PostHog) to react to consent updates.
   */
  dispatchPreferencesUpdated(preferences) {
    try {
      document.dispatchEvent(
        new CustomEvent("cookie:preferences-updated", {
          detail: { preferences }
        })
      );
    } catch (e) {
      console.error("Failed to dispatch cookie:preferences-updated event:", e);
    }
  }
}
